/**
 * @author jakubmaslon
 * @since 2020-10-27
 */

import * as React from "react";
import styled from "styled-components";

import { EMPLOYEE as EMPLOYEE_TEST_IDS } from "@config/testIds/employee";

import {
  Badge,
  Tags,
  Heading,
  SelectButton,
  RoundPicture,
  TestWrapper,
} from "@ui/Atoms";
import { Rating } from "@ui/Molecules";
import { Time } from "@ui/Assets/Symbolicons/Time";
import { Target } from "@ui/Assets/Symbolicons/Target";
import { IconVariant } from "@ui/Assets/Symbolicons/Symbolicons";
import theme from "@ui/themes/default";
import { rem, rgba } from "@ui/helpers";

import { useBreakpoint } from "@hooks/useBreakpoint";

interface RatingProps {
  rating: number;
}

const RatingWithValue = (props: RatingProps) => (
  <Rate>
    <Rating
      value={Math.floor(props.rating)}
      count={5}
      locked
      onClick={() => null}
      size={12}
      color={theme.palette.blue400}
      withNoMargin
    />
    <RatingValue>{props.rating}</RatingValue>
  </Rate>
);

interface StatsComponentProps {
  cleaningsDone: string;
  acceptanceRate: number;
  translations: {
    cleaningsDoneText: string;
    acceptanceRateText: string;
  };
}

const StatsComponent = (props: StatsComponentProps) => {
  const statsIconStyle = {
    variant: "line" as IconVariant,
    size: 24,
    height: 24,
    color: theme.palette.grey400,
  };

  return (
    <Stats>
      <Stat>
        <Time {...statsIconStyle} />
        <StatLabel>{`${props.cleaningsDone} ${props.translations.cleaningsDoneText}`}</StatLabel>
      </Stat>
      <Stat>
        <Target {...statsIconStyle} />
        <StatLabel>{`${props.acceptanceRate}% ${props.translations.acceptanceRateText}`}</StatLabel>
      </Stat>
    </Stats>
  );
};

interface EmployeeDetails {
  uuid: string;
  displayName: string;
  photoUrl: string | null;
  acceptanceRate: number;
  cleaningsDone: string;
  rating: number;
  isVerified: boolean;
  workPermit: string;
  doesIroning: boolean;
  hasVehicle: boolean;
  languages: string[];
}

interface Props {
  employee: EmployeeDetails;
  isSelected?: boolean;
  onChooseThisAgent: (id: string) => void;
  translations: {
    ironingText: string;
    hasVehicleText: string;
    verifiedProfileText: string;
    selectText: string;
    cleaningsDoneText: string;
    acceptanceRateText: string;
  };
}

const Employee = (props: Props): React.ReactElement => {
  const { mobile } = useBreakpoint("v1");

  const tags: string[] = [
    ...props.employee.languages,
    ...(props.employee.doesIroning ? [props.translations.ironingText] : []),
    ...(props.employee.hasVehicle ? [props.translations.hasVehicleText] : []),
  ];

  return (
    <TestWrapper testId={EMPLOYEE_TEST_IDS.WRAPPER}>
      <EmployeeStyled isSelected={props.isSelected}>
        {mobile ? (
          <React.Fragment>
            <EmployeePictureAndData>
              <RoundPicture picturePath={props.employee.photoUrl} />
              <EmployeeData>
                <Name type="h6">{props.employee.displayName}</Name>
                <RatingWithValue rating={props.employee.rating} />
              </EmployeeData>
            </EmployeePictureAndData>
            <BadgeContainerMobile>
              {props.employee.isVerified && (
                <Badge
                  testId={EMPLOYEE_TEST_IDS.VERIFIED_PROFILE_BADGE}
                  label={props.translations.verifiedProfileText}
                />
              )}
            </BadgeContainerMobile>
            <StatsComponent
              cleaningsDone={props.employee.cleaningsDone}
              acceptanceRate={props.employee.acceptanceRate}
              translations={{
                cleaningsDoneText: props.translations.cleaningsDoneText,
                acceptanceRateText: props.translations.acceptanceRateText,
              }}
            />
            <EmployeeTagsAndSelect>
              <Tags tags={tags} />
              <SelectButtonStyled
                active={props.isSelected}
                withCheckMark
                onClick={() => props.onChooseThisAgent(props.employee.uuid)}
              >
                {props.translations.selectText}
              </SelectButtonStyled>
            </EmployeeTagsAndSelect>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <EmployeePictureAndData>
              <RoundPicture picturePath={props.employee.photoUrl} />
              <EmployeeData>
                <TopRow>
                  <Name type="h6">{props.employee.displayName}</Name>
                  {props.employee.isVerified && (
                    <Badge
                      testId={EMPLOYEE_TEST_IDS.VERIFIED_PROFILE_BADGE}
                      label={props.translations.verifiedProfileText}
                    />
                  )}
                </TopRow>
                <BottomRow>
                  <RatingWithValue rating={props.employee.rating} />
                  <StatsComponent
                    cleaningsDone={props.employee.cleaningsDone}
                    acceptanceRate={props.employee.acceptanceRate}
                    translations={{
                      cleaningsDoneText: props.translations.cleaningsDoneText,
                      acceptanceRateText: props.translations.acceptanceRateText,
                    }}
                  />
                </BottomRow>
              </EmployeeData>
            </EmployeePictureAndData>
            <EmployeeTagsAndSelect>
              <Tags tags={tags} />
              <SelectButtonStyled
                testId={EMPLOYEE_TEST_IDS.BUTTON}
                active={props.isSelected}
                withCheckMark
                onClick={() => props.onChooseThisAgent(props.employee.uuid)}
              >
                {props.translations.selectText}
              </SelectButtonStyled>
            </EmployeeTagsAndSelect>
          </React.Fragment>
        )}
      </EmployeeStyled>
    </TestWrapper>
  );
};

Employee.displayName = "Employee";

export { Employee };

const EmployeeStyled = styled.div<{ isSelected?: boolean }>`
  padding: ${props => props.theme.margins.base_x4};
  box-shadow: ${props =>
    `0 ${rem(2)} ${rem(3)} 0 ${rgba(props.theme.palette.grey700, 0.2)}`};
  border: ${props => (props.isSelected ? "4px" : "1px")} solid
    ${props => props.theme.palette.blue500};
  border-radius: ${props => props.theme.buttons.borderRadius};
`;

const Name = styled(Heading)`
  font-weight: ${props => props.theme.fonts.weights.semibold};
  margin: 0;
`;

const RatingValue = styled.span`
  margin-left: ${props => props.theme.margins.base};
  font-size: ${props => props.theme.fonts.sizes.caption};
  color: ${props => props.theme.palette.grey400};
`;

const Stat = styled.div`
  display: flex;
  align-items: center;
  margin: 0 ${props => props.theme.margins.half};
`;

const StatLabel = styled.div`
  color: ${props => props.theme.palette.grey400};
  font-size: ${props => props.theme.fonts.sizes.caption};
`;

const EmployeePictureAndData = styled.div`
  display: flex;
  flex: 1 0 0;
`;

const EmployeeData = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-left: ${props => props.theme.margins.base_x3};
`;

const BadgeContainerMobile = styled.div`
  display: inline-block;
  align-self: flex-start;
  margin: ${props => props.theme.margins.base} 0;
`;

const Stats = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 -${props => props.theme.margins.half};

  ${props => props.theme.breakpoints.desktopLarge} {
    flex-direction: row;
  }
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.margins.base};
`;

const BottomRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Rate = styled.div`
  display: flex;
  align-items: flex-end;
  margin-top: ${props => props.theme.margins.base};

  ${props => props.theme.breakpoints.tablet} {
    margin-top: 0;
  }
`;

const EmployeeTagsAndSelect = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${props => props.theme.margins.base};

  ${props => props.theme.breakpoints.desktopLarge} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const SelectButtonStyled = styled(SelectButton)`
  align-self: center;
  margin-top: ${props => props.theme.margins.base};
  width: calc(50% - ${rem(1)});

  ${props => props.theme.breakpoints.tablet} {
    width: calc(25% - ${rem(1)});
  }

  ${props => props.theme.breakpoints.desktopLarge} {
    margin-top: 0;
  }
`;
